<template>
  <b-container fluid class="no-bottom">
    <div class="bottom-actions scan-bar">
      <b-button v-if="inSaving" variant="outline-secondary" disabled>Enregistrement...</b-button>
      <template v-else>
        <!-- QR-004 : dépannage quand l'étiquette est illisible. La douchette reste le
             mode nominal, sélectionné par défaut. Même présentation qu'en préparation. -->
        <ScanModeSwitch v-model="scanMode" class="scan-mode" />
        <!-- QR-006 : en mode photo, le viseur prend la place du champ. Il n'en occupe
             que la hauteur utile, pour laisser voir la liste, et masquer le champ évite
             de faire surgir le clavier du mobile sur un écran où l'on ne tape pas. -->
        <CameraScanner v-if="cameraEntry" @decoded="onCameraDecoded" @close="closeCameraScanner" />
        <b-button-group v-else>
          <input type="text" v-model="internal_code"
            :placeholder="manualEntry ? 'Saisir un code référence' : 'Scanner une référence'"
            class="form-control" ref="internal_code" @keyup.enter="submitManualEntry()" />
          <b-button v-if="manualEntry" variant="outline-secondary" :disabled="!internal_code"
            @click="submitManualEntry()" style="border-left: 1px solid #e9e9e9">OK</b-button>
        </b-button-group>
      </template>
    </div>
    <b-row class="d-flex">
      <b-col class="d-flex flex-grow-1">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            Demande de retour
            <div v-if="!isBusy" class="subtitle">#{{ values.return_number }}</div>
            <div v-if="!isBusy" class="subtitle">
              <i class="far fa-warehouse"></i>
              {{ values.return_warehouse ? values.return_warehouse.name : "Entrepôt non déterminé" }}
            </div>
          </h1>
        </div>
      </b-col>
      <b-col class="d-flex flex-grow-0">
        <b-button
          v-b-tooltip.hover
          title="Signalement utilisateur"
          variant="danger"
          v-b-modal.modal-alert
          v-if="values.failure_warning_comment"
          class="danger-notif"
        >
          <i class="far fa-bell fa-solid"></i>
        </b-button>
      </b-col>
    </b-row>
    <div v-if="isBusy" class="loader"></div>

    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <h2 :class="[values.group_slug]">
          <i class="far fa-wrench"></i>
          Références uniques
        </h2>

        <div class="table-wrapper">

          <b-table ref="table" hover show-empty :fields="fields" :items="values.unique_references">
            <template slot="empty" slot-scope="scope">
              <div class="text-center mt-2">Cette liste est vide pour le moment</div>
            </template>

            <template slot="product" slot-scope="data">
              <router-link
                v-if="$store.getters['auth/isAuthorized']('product_show')"
                :to="{ name: 'ProductMobileDetail', params: { id: data.value.id } }"
                >{{ data.value.name }}</router-link
              >
              <template v-else>{{ data.value.name }}</template>
            </template>
            <b-button-group slot="actions" slot-scope="data" size="sm" class="actions">
              <b-button
                v-b-tooltip.hover
                title="Retirer"
                variant="outline-secondary"
                v-b-modal.modal-remove
                v-if="
                  data.item.status &&
                  data.item.status.status_slug == 'reference-en-location' &&
                  !removed_refs.includes(data.item.id)
                "
                @click="reference_id = data.item.id"
              >
                <i class="far fa-trash-alt"></i>
              </b-button>
              <b-badge
                v-if="data.item.status && data.item.status.status_slug == 'reference-en-stock'"
                variant="success"
              >
                <i class="fa fa-check"></i>
                En stock
              </b-badge>
              <b-badge
                v-if="data.item.status && data.item.status.status_slug == 'reference-en-attente-de-maintenance'"
                variant="warning"
              >
                <i class="fa fa-wrench"></i>
                En attente de maintenance
              </b-badge>
              <b-badge v-if="removed_refs.includes(data.item.id)" variant="danger">
                <i class="fa fa-trash-alt"></i>
                Sera retirée
              </b-badge>
            </b-button-group>
          </b-table>
        </div>
      </b-col>
    </b-row>

    <div class="bottom-actions">
      <b-button
        :variant="inSaving ? 'outline-secondary' : 'outline-primary'"
        :disabled="inSaving"
        @click="valideReturn()"
      >
        {{ inSaving ? "Enregistrement..." : "Clôturer la demande" }}
        <i v-if="!inSaving" class="fa fa-chevron-right"></i>
      </b-button>
    </div>

    <b-modal id="modal-alert" hide-footer centered no-close-on-backdrop v-if="values.failure_warning_comment">
      <template slot="modal-title">Signalement utilisateur / produit défectueux</template>
      <span v-if="values.failure_warning_comment" class="d-flex"><strong>Signalement : </strong><label class="pre-formatted m-l-xs">{{ values.failure_warning_comment }}</label></span>
    </b-modal>
    <b-modal id="modal-remove" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Retirer de la demande</template>
      <div class="d-block text-center">
        <h3>Êtes-vous sur de vouloir retirer cette références unique de la demande ?</h3>
      </div>
      <b-button class="mt-3" variant="success" block @click="removeRef()">Confirmer</b-button>
      <b-button class="mt-3" variant="outline-secondary" block @click="cancelRemoveRef()"
        >Annuler</b-button
      >
    </b-modal>
  </b-container>
</template>

<script>
import scannedValueMixin from "@/mixins/scannedValue";
import { environmentLabel } from "@/components/StorageEnvironment/labels";

export default {
  name: "BusinessReturnMobileDetail",
  mixins: [scannedValueMixin],

  data() {
    return {
      fields: [
        {
          key: "product",
          label: "Produit",
        },
        {
          key: "internal_code",
          label: "Code interne",
        },
        {
          // Emplacement du produit dans l'entrepôt de réception, pour guider le rangement.
          key: "storage_environment",
          label: "Emplacement",
          formatter: (value) => environmentLabel(value, false) || "-",
        },
        {
          key: "actions",
          label: "Actions",
        },
      ],
      reference_id: null,
      isBusy: true,
      inSaving: false,
      removed_refs: [],
      preparator_comment: null,
      internal_code: null,
      scanned_quantity: 0,
      product_id: null,
      inScanning: false,
      scanned_product: null,
      quantity_error: false,
      scanned_references: []
    };
  },
  computed: {
    values: function () {
      return this.$store.getters["business_return/getItem"](this.$route.params.id) || {};
    },
  },
  mounted() {
    this.isBusy = true;
    this.$store.dispatch("business_return/getItem", { id: this.$route.params.id }).then(() => {
      // Test si le contenu demandé est bien trouvé
      let self = this;
      if (Object.entries(this.values).length <= 0) {
        this.$toastr.error(
          "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
        );
        setTimeout(function () {
          self.$router.go(-1);
        }, 5000);
        return;
      }
      this.isBusy = false;
      // Le champ de scan n'est rendu qu'une fois le chargement terminé :
      // le focus ne peut être posé qu'après ce rendu.
      this.$nextTick(() => {
        if (this.$refs.internal_code) {
          this.$refs.internal_code.focus();
        }
      });
    });
    this.$store.dispatch("reference/getAllForSelect");
  },
  watch: {
    internal_code: function (internal_code) {
      // QR-004 : en saisie manuelle, la validation est explicite (bouton / Entrée).
      if (internal_code && !this.manualEntry) {;
        // La douchette tape caractère par caractère : on attend la fin de la salve.
        this.handleScannedValueDebounced(internal_code);
      }
    },
  },
  methods: {
    // QR-003 : appelé par le mixin une fois la valeur normalisée.
    onScanResolved: function (code) {
      this.getReference(code);
    },
    /**
     * Cherche une référence ou un produit à la vente à partir de son code barre
     * @param   {string|interger} internal_code  Code barre
     * @returns                             Si les tests sont passés, appelle la méthode addRéférence
     */
    getReference: function (internal_code) {
      if (this.inScanning == true) {
        this.internal_code = null;
        return
      }

      this.inScanning = true
      this.$store.dispatch("reference/getItemByInternalCode", internal_code).then((reference) => {
        if (!reference.id) {
          this.inScanning = false
          this.$toastr.error(
            "Le code barre scanné ne correspond à aucune référence"
          );
          return false
        }
        let business_return_reference = this.values.unique_references.find(
          (item) => item.id == reference.id
        );

        // Si la référence correspond bien à un matériel demandé
        if (business_return_reference) {
          this.inScanning = false
          this.updateRef('reference-en-attente-de-maintenance', reference.id)
        } else {
          this.inScanning = false
          this.$toastr.error("La référence scannée ne correspond à aucun matériel à retourner");
        }
      })

      this.internal_code = null;
    },
    cancelValideRef() {
      this.$root.$emit("bv::hide::modal", "modal-valide");
    },
    cancelSendMaintenance() {
      this.$root.$emit("bv::hide::modal", "modal-send-maintenance");
    },
    cancelRemoveRef() {
      this.$root.$emit("bv::hide::modal", "modal-remove");
    },
    updateRef(status_slug, reference_id) {
      if (reference_id) {
        this.$store
          .dispatch("reference/getItem", { id: reference_id, wait: true })
          .then(() => {
            let reference = this.$store.getters["reference/getItem"](reference_id);

            reference = {
              ...reference,
              product_id: reference.product ? reference.product.id : null,
              supplier_id: reference.supplier ? reference.supplier.id : null,
              status_slug: status_slug,
              purchase_date: reference.purchase_date
                ? this.$moment(reference.purchase_date).format("YYYY-MM-DD")
                : null,
              first_maintenance_date: reference.first_maintenance_date
                ? this.$moment(reference.first_maintenance_date).format("YYYY-MM-DD")
                : null,
              last_maintenance_date: reference.last_maintenance_date
                ? this.$moment(reference.last_maintenance_date).format("YYYY-MM-DD")
                : null,
            };

            // Réception : la référence rejoint l'entrepôt de dépôt du retour, à défaut celui de l'opérateur
            if (status_slug === 'reference-en-attente-de-maintenance') {
              let warehouse = this.values.return_warehouse || this.$store.getters["auth/warehouse"];
              reference.reception_warehouse_id = warehouse ? warehouse.id : null;
            }

            this.$store.dispatch("reference/saveItem", reference).then((response) => {
              this.$store.dispatch("business_return/getItem", {
                id: this.$route.params.id,
              });

            });
          });
      }
    },
    removeRef() {
      if (this.reference_id) {
        this.removed_refs.push(this.reference_id);
        this.$root.$emit("bv::hide::modal", "modal-remove");
      }
    },
    valideReturn() {
      this.inSaving = true;

      // Vérification que toutes les refs sont bien remises en stock
      let unvalidated_references = this.values.unique_references.filter(
        (item) =>
          item.status &&
          item.status.status_slug == "reference-en-location" &&
          !this.removed_refs.includes(item.id)
      );

      if (unvalidated_references.length) {
        this.$toastr.error(
          "Toutes les références doivent être contrôlées pour clôturer la demande"
        );
        this.inSaving = false;
        return;
      }

      let business_return = {
        ...this.values,
        agency_number: this.values.agency ? this.values.agency.internal_number : null,
        business_number: this.values.business ? this.values.business.number : null,
        status_slug: "business-return-receptionnee",
        withdraw_wished_date: this.values.withdraw_wished_date
          ? this.$moment(this.values.withdraw_wished_date).format("YYYY-MM-DD")
          : null,
        axiloc_creation_date: this.values.axiloc_creation_date
          ? this.$moment(this.values.axiloc_creation_date).format("YYYY-MM-DD")
          : null,
        unique_references: this.values.unique_references.filter(
          (ref) => !this.removed_refs.includes(ref.id)
        ),
        user_id: this.values.user ? this.values.user.id : null,
      };

      this.$store.dispatch("business_return/saveItem", business_return).then((response) => {
        this.inSaving = false;
        this.$router.push({ name: "BusinessReturnMobileList" });
      });
    },
  },
};
</script>


<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.danger-notif{ background-color: #dc3545 !important; }

.wrapper-content {
  padding-bottom: 25px;
}

.bottom-actions {
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.05);
}

// Zone de scan / saisie manuelle : même barre qu'en préparation
// (RentingPreparationMobileDetail), d'où la classe `bottom-actions` conservée pour
// en hériter tous les styles. Seul le positionnement est neutralisé : la barre
// « Clôturer la demande » est déjà collée en haut, deux barres sticky se
// chevaucheraient.
.bottom-actions.scan-bar {
  position: static;
  // Encadrée plutôt que posée en surplomb : elle reste dans le flux, l'ombre de la
  // barre sticky n'a pas lieu d'être ici.
  box-shadow: none;
  border: 1px solid #eeeeee;
  margin-bottom: 15px;
}

.scan-bar {
  .btn-group {
    width: 100%;
  }

  input {
    border-radius: 0;
    border: none;
    padding: 5px;
    margin: 20px 25px;
    @extend .clanprobook;
  }

  // Le champ de scan porte déjà la marge basse : le sélecteur de mode se cale
  // sur son retrait horizontal sans en rajouter.
  .scan-mode {
    margin: 20px 25px 0;
  }
}

.pre-formatted {
  white-space: pre;
}

</style>



// WEBPACK FOOTER //
// src/components/BusinessReturnMobile/BusinessReturnMobileDetail.vue